/**
 * Bat Mitzvah slideshow polish (templates/collection.bat-mitzvah.liquid), layered on <s-slideshow id="slideshow-bm">:
 * - Marks the shown slide with [data-bm-on] so its copy arrives line by line (CSS .bm-stagger / .bm-kb).
 * - Fills a thin gold line on the active dot for as long as that slide stays up (.bm-prog).
 * - "Type her name": writes the name into "You've always been a Bat Melech, ___."
 * Reduced motion: no arrivals or timing line (class bm-js is not added).
 */
const show = document.getElementById('slideshow-bm');

if (show) {
  const motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (motionOk) show.classList.add('bm-js');

  const slides = () => Array.from(show.querySelectorAll('[data-slide]'));
  const intervalFor = (slide) => {
    const own = parseInt(slide?.dataset.autoplayInterval, 10);
    return own > 0 ? own : parseInt(show.getAttribute('autoplay-interval'), 10) || 5000;
  };
  let current = 0;

  const startProgress = () => {
    show.querySelectorAll('.bm-prog').forEach((el) => el.remove());
    if (!motionOk) return;
    const dot = show.querySelectorAll('[data-slideshow-dots] button')[current];
    if (!dot) return;
    const bar = document.createElement('span');
    bar.className = 'bm-prog';
    bar.setAttribute('aria-hidden', 'true');
    bar.style.animationDuration = `${intervalFor(slides()[current])}ms`;
    dot.appendChild(bar);
  };

  const activate = (index) => {
    current = index;
    slides().forEach((slide, i) => slide.toggleAttribute('data-bm-on', i === index));
    startProgress();
  };

  show.addEventListener('slideshow:change', (event) => activate(event.detail.index));

  // Autoplay holds while hovered or focused and restarts its countdown after; mirror that on the line.
  const hold = () => show.setAttribute('data-bm-hold', '');
  const release = () => {
    if (show.matches(':hover') || show.contains(document.activeElement)) return;
    show.removeAttribute('data-bm-hold');
    startProgress();
  };
  show.addEventListener('mouseenter', hold);
  show.addEventListener('mouseleave', release);
  show.addEventListener('focusin', hold);
  show.addEventListener('focusout', () => setTimeout(release, 0));
  new MutationObserver(startProgress).observe(show, { attributes: true, attributeFilter: ['autoplay'] });

  // Phones: move on right after the charm comes together (skip the worn-photo ending). Desktop keeps the full 21s story.
  const firstSlide = slides()[0];
  if (firstSlide && window.matchMedia('(max-width: 767px)').matches) {
    const intro = document.querySelector('s-bm-intro [data-intro]');
    firstSlide.dataset.autoplayInterval = intro ? '12500' : '9000';
    if (intro) {
      const introHost = intro.parentElement;
      new MutationObserver((records, observer) => {
        if (!introHost.querySelector('[data-intro]')) { firstSlide.dataset.autoplayInterval = '9000'; observer.disconnect(); }
      }).observe(introHost, { childList: true });
    }
    // Restart the first countdown so it uses the shorter phone timing.
    customElements.whenDefined('s-slideshow').then(() => {
      if (!show.hasAttribute('autoplay') || current !== 0) return;
      const restart = new Event('command');
      restart.command = '--play';
      show.dispatchEvent(restart);
    });
  }

  customElements.whenDefined('s-slideshow').then(() => requestAnimationFrame(() => activate(current)));

  // Her name: English or Hebrew (on-screen letter pad + 3 Hebrew style previews). Preview only; the charm itself has no name.
  const namer = show.querySelector('[data-bm-namer]');
  const target = show.querySelector('[data-bm-name]');
  if (namer && target) {
    const enInput = namer.querySelector('[data-bm-name-input]');
    const heInput = namer.querySelector('[data-bm-name-input-he]');
    const hePanel = namer.querySelector('[data-bm-he]');
    const cta = namer.querySelector('[data-bm-name-cta]');
    const ctaEn = namer.querySelector('[data-bm-cta-en]');
    const ctaHe = namer.querySelector('[data-bm-cta-he]');
    const extras = show.querySelectorAll('[data-bm-hide-he]');
    let mode = 'en';
    let font = "'Frank Ruhl Libre', serif";

    const cleanEn = (v) => v.replace(/\s+/g, ' ').trim().slice(0, 18);
    const cleanHe = (v) => v.replace(/[^\u05D0-\u05EA\s'"\u05F3\u05F4-]/g, '').replace(/\s+/g, ' ').slice(0, 12);

    const render = () => {
      const name = mode === 'he' ? heInput.value.trim() : cleanEn(enInput.value);
      target.textContent = '';
      if (name) {
        target.append(', ');
        if (mode === 'he') {
          const bdi = document.createElement('bdi');
          bdi.dir = 'rtl';
          bdi.lang = 'he';
          bdi.style.fontFamily = font;
          bdi.textContent = name;
          target.append(bdi);
        } else {
          target.append(name);
        }
      }
      target.classList.toggle('bm-name-on', Boolean(name));
      if (cta) cta.hidden = !name;
      if (ctaEn) ctaEn.hidden = mode === 'he';
      if (ctaHe) ctaHe.hidden = mode !== 'he';
    };

    const setMode = (next) => {
      mode = next;
      namer.dataset.mode = next;
      namer.querySelectorAll('[data-bm-lang]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.bmLang === next)));
      enInput.hidden = next === 'he';
      hePanel.hidden = next !== 'he';
      extras.forEach((el) => { el.hidden = next === 'he'; });
      render();
    };

    namer.querySelectorAll('[data-bm-lang]').forEach((b) => b.addEventListener('click', () => setMode(b.dataset.bmLang)));
    enInput.addEventListener('input', render);
    enInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') enInput.blur(); });
    heInput.addEventListener('input', () => { heInput.value = cleanHe(heInput.value); render(); });

    namer.querySelectorAll('[data-key]').forEach((key) => key.addEventListener('click', () => {
      const k = key.dataset.key;
      let v = heInput.value;
      if (k === 'back') v = v.slice(0, -1);
      else if (k === ' ') v = v && !v.endsWith(' ') ? `${v} ` : v;
      else v += k;
      heInput.value = cleanHe(v);
      render();
    }));

    namer.querySelectorAll('[data-bm-font]').forEach((b) => b.addEventListener('click', () => {
      font = b.dataset.bmFont;
      namer.querySelectorAll('[data-bm-font]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      heInput.style.fontFamily = font;
      namer.querySelectorAll('[data-bm-keys] [data-key]').forEach((x) => { x.style.fontFamily = font; });
      render();
    }));
  }
}

/**
 * Phones only: the slideshow track hugs the slide on screen, so a short slide (like "Her moment")
 * doesn't sit on extra pink space sized for the tallest slide. Desktop keeps its fixed height.
 */
const fitShow = document.getElementById('slideshow-bm');
if (fitShow) {
  const track = fitShow.querySelector('[data-slideshow-track]');
  const phone = window.matchMedia('(max-width: 767px)');
  const active = () => fitShow.querySelector('[data-slide][aria-hidden="false"]') || fitShow.querySelector('[data-slide]');
  const fit = () => {
    if (!track) return;
    if (!phone.matches) { track.style.height = ''; return; }
    const slide = active();
    if (!slide) return;
    // Measure the slide's content, not the slide box, so a short track can never clip it.
    const inner = Array.from(slide.children).reduce((sum, child) => sum + child.scrollHeight, 0);
    track.style.height = `${Math.ceil(Math.max(slide.scrollHeight, inner, slide.getBoundingClientRect().height)) + 2}px`;
  };
  fitShow.addEventListener('slideshow:change', () => {
    requestAnimationFrame(fit);
    setTimeout(fit, 500);
    setTimeout(fit, 1500);
  });
  if (track) {
    track.style.alignItems = 'flex-start';
    track.style.transition = 'height 0.4s ease';
    new MutationObserver(fit).observe(fitShow, { subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });
    const ro = new ResizeObserver(fit);
    fitShow.querySelectorAll('[data-slide]').forEach((s) => ro.observe(s));
    phone.addEventListener('change', () => { track.style.alignItems = phone.matches ? 'flex-start' : ''; fit(); });
    if (!phone.matches) track.style.alignItems = '';
    fit();
    // Re-measure once photos and fonts land so the track never clips the bottom of a slide.
    fitShow.querySelectorAll('[data-slide] img').forEach((img) => { if (!img.complete) img.addEventListener('load', fit, { once: true }); });
    document.fonts?.ready.then(fit);
    window.addEventListener('load', fit, { once: true });
    fitShow.addEventListener('animationend', fit);
  }
}
